import {EnumValue} from '@/components/shared/EnumValue'
import {BadgeVariant} from '@/components/ui/badge'
import {TriggerSource} from '@/types/identity'

/**
 * Rich enum carrying badge variant per {@link TriggerSource} value (spec §8.5).
 *
 * <p>Pages look up the variant via {@code TriggerSourceBadge.of(triggerSource)}
 * and read {@code .variant} directly — no parallel switch in the UI layer.
 * Localized labels live in i18n (`lifecycleEvents.triggerSource.*`); this class
 * is the single source of truth for "how does this trigger source look".
 */
export class TriggerSourceBadge extends EnumValue {
    constructor(code: TriggerSource, variant: BadgeVariant) {
        super(code, variant)
    }

    static readonly USER_ACTION = new TriggerSourceBadge(TriggerSource.USER_ACTION, BadgeVariant.DEFAULT)
    static readonly JML_JOINER = new TriggerSourceBadge(TriggerSource.JML_JOINER, BadgeVariant.SUCCESS)
    static readonly JML_LEAVER = new TriggerSourceBadge(TriggerSource.JML_LEAVER, BadgeVariant.DESTRUCTIVE)
    static readonly JML_MOVER = new TriggerSourceBadge(TriggerSource.JML_MOVER, BadgeVariant.SECONDARY)
    static readonly EXIT_DATE_JOB = new TriggerSourceBadge(TriggerSource.EXIT_DATE_JOB, BadgeVariant.WARNING)
    static readonly REACTIVATION_SWEEPER = new TriggerSourceBadge(TriggerSource.REACTIVATION_SWEEPER, BadgeVariant.SUCCESS)
    static readonly INBOUND_SYNC = new TriggerSourceBadge(TriggerSource.INBOUND_SYNC, BadgeVariant.SECONDARY)
    static readonly OUTBOUND_PROVISIONING = new TriggerSourceBadge(TriggerSource.OUTBOUND_PROVISIONING, BadgeVariant.SECONDARY)
    static readonly ACCESS_REQUEST = new TriggerSourceBadge(TriggerSource.ACCESS_REQUEST, BadgeVariant.DEFAULT)
    static readonly CERTIFICATION_REVOKE = new TriggerSourceBadge(TriggerSource.CERTIFICATION_REVOKE, BadgeVariant.WARNING)
    static readonly INITIAL_LOAD = new TriggerSourceBadge(TriggerSource.INITIAL_LOAD, BadgeVariant.OUTLINE)
    static readonly SYSTEM = new TriggerSourceBadge(TriggerSource.SYSTEM, BadgeVariant.OUTLINE)

    static readonly values: ReadonlyArray<TriggerSourceBadge> = [
        TriggerSourceBadge.USER_ACTION,
        TriggerSourceBadge.JML_JOINER,
        TriggerSourceBadge.JML_LEAVER,
        TriggerSourceBadge.JML_MOVER,
        TriggerSourceBadge.EXIT_DATE_JOB,
        TriggerSourceBadge.REACTIVATION_SWEEPER,
        TriggerSourceBadge.INBOUND_SYNC,
        TriggerSourceBadge.OUTBOUND_PROVISIONING,
        TriggerSourceBadge.ACCESS_REQUEST,
        TriggerSourceBadge.CERTIFICATION_REVOKE,
        TriggerSourceBadge.INITIAL_LOAD,
        TriggerSourceBadge.SYSTEM,
    ]

    static of(code: TriggerSource): TriggerSourceBadge {
        return TriggerSourceBadge.values.find((v) => v.code === code) ?? TriggerSourceBadge.SYSTEM
    }
}
